
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import CancelButton from "../../../Components/CommonComponent/Button/CancelButton";
import SaveButton from "../../../Components/CommonComponent/Button/SaveButton";
import { useTranslation } from "react-i18next";
import googleTransliterate from "../../../utils/googleTransliterate";
import { FaEdit } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { FaChevronDown } from "react-icons/fa";

/** 🔹 Reusable Dropdown (memoized) */
const Dropdown = React.memo(function Dropdown({
  value,
  options,
  placeholder,
  onSelect,
  extraOption,
  hasError,
}) {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [usingKeyboard, setUsingKeyboard] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (opt) => {
      onSelect(opt);
      setOpen(false);
      setHighlightedIndex(-1);
      setUsingKeyboard(false);
    },
    [onSelect]
  );

  const handleExtraClick = useCallback(() => {
    if (extraOption?.onClick) extraOption.onClick();
    setOpen(false);
    setHighlightedIndex(-1);
    setUsingKeyboard(false);
  }, [extraOption]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setOpen(true);
        setUsingKeyboard(true);
        setHighlightedIndex(0);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setUsingKeyboard(true);
      setHighlightedIndex((prev) =>
        prev < (options.length + (extraOption ? 1 : 0)) - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setUsingKeyboard(true);
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : (options.length + (extraOption ? 1 : 0)) - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < options.length) {
        handleSelect(options[highlightedIndex].value);
      } else if (extraOption && highlightedIndex === options.length) {
        handleExtraClick();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlightedIndex(-1);
    }
  };

  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const item = listRef.current.children[highlightedIndex];
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  return (
    <div className="relative w-full mb-1" ref={dropdownRef}>
      <div
        tabIndex={0}
        onClick={() => { setOpen((prev) => !prev); setUsingKeyboard(false); }}
        onKeyDown={handleKeyDown}
        className={`border ${hasError ? "border-danger" : "border-gray-300"} 
              rounded px-2 py-1 cursor-pointer bg-white focus:outline-none
              flex justify-between items-center`}
      >
        <span className="truncate">{value || placeholder}</span>
        <FaChevronDown className={`ml-2 text-sm transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>

      {open && (
        <ul ref={listRef} className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded shadow max-h-40 overflow-y-auto p-0">
          {options.map((opt, i) => (
            <li
              key={i}
              onClick={() => handleSelect(opt.value)}
              onMouseEnter={() => { if (!usingKeyboard) setHighlightedIndex(i); }}
              className={`px-4 py-1 cursor-pointer capitalize border-dashed border-b ${highlightedIndex === i
                  ? "bg-theme-headerBg text-theme-color"
                  : !usingKeyboard
                    ? "hover:bg-theme-headerBg hover:text-theme-color"
                    : ""
                }`}
            >
              {opt.label} {language !== "en" && opt.labelInHindi && ` ( ${opt.labelInHindi} )`}
            </li>
          ))}

          {extraOption && (
            <li
              onClick={handleExtraClick}
              onMouseEnter={() => { if (!usingKeyboard) setHighlightedIndex(options.length); }}
              className={`px-4 py-1 cursor-pointer border-dashed border-b capitalize ${highlightedIndex === options.length
                  ? "bg-theme-headerBg text-theme-color fw-medium"
                  : !usingKeyboard
                    ? "fw-medium text-theme-tabHoverColor hover:bg-theme-headerbg hover:text-theme-color"
                    : "fw-medium text-theme-tabHoverColor"
                }`}
            >
              {extraOption.label}
            </li>
          )}
        </ul>
      )}
    </div>
  );
});


export default function CreateSubGroup({
  subGroupName,
  subGroupNameInHindi,
  setSubGroupNameInHindi,
  setSubGroupName,
  subGroupValidation,
  groupName,
  setGroupName,
  groupId,
  setGroupId,
  selectGroupValidation,
  groupType,
  setGroupType,
  setIsGroupModalOpen,
  handleGroupSelect,
  handleSubGroupCancel,
  handleSave,
  loading,
  setLoading,
  allGroupsData,
  selectedSubGroup,
  setSubGroupValidation,
  setSelectGroupValidation,
  addMoreButton,
  isEditMode,
  subGroupBatch,
  setSubGroupBatch,
}) {
  const { t } = useTranslation();
  const [showField, setShowField] = React.useState(false);
  const [hindiSuggestion, setHindiSuggestion] = React.useState("");
  const [editingBatchIndex, setEditingBatchIndex] = React.useState(-1);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  useEffect(() => {
    if (groupName && allGroupsData?.length > 0) {
      const foundGroup = allGroupsData.find(
        (g) => g.groupName === groupName || (g.id || g.group_id) === groupId
      );
      if (foundGroup && !groupType) {
        setGroupType(foundGroup.type || "");
      }
    }
  }, [groupName, groupId, allGroupsData]);


  const options = useMemo(
    () =>
      (allGroupsData || []).map((g) => ({
        key: g.id || g.group_id,
        value: { name: g.groupName, id: g.id || g.group_id },
        label: g.groupName,
        labelInHindi: g.groupNameInHindi,
      })),
    [allGroupsData]
  );

  const handleAddNewGroup = useCallback(() => {
    setIsGroupModalOpen(true);
    setGroupName("");
    setGroupId("");
    setGroupType("");
  }, [setIsGroupModalOpen, setGroupName, setGroupType, setGroupId]);

  const handleSelectGroup = useCallback(
    (selected) => {
      setGroupName(selected.name);
      setGroupId(selected.id);
      handleGroupSelect(selected.name);
    },
    [handleGroupSelect, setGroupName, setGroupId]
  );

  const handleSubGroupNameChange = async (e) => {
    const value = e.target.value;
    setSubGroupName(value);
    setHindiSuggestion(value);

    try {
      if (language !== "en") {
        const result = await googleTransliterate(value, language);

        if (result?.suggestions) {
          setSuggestions(result.suggestions.slice(0, 4)); // only 3 suggestions
          setSubGroupNameInHindi(result.suggestions[0] || "");
        } else if (typeof result === "string") {
          setSuggestions([result]);
          setSubGroupNameInHindi(result);
        }

        setShowSuggestions(true);
      }
    } catch (err) {
      console.error("Transliteration error:", err);
    }
  };

  const handleConvertNameInHindi = async (e) => {
    const value = e.target.value;
    setHindiSuggestion(value);

    try {
      const result = await googleTransliterate(value, language);

      if (result?.suggestions) {
        setSuggestions(result.suggestions.slice(0, 3));
        setSubGroupNameInHindi(result.suggestions[0] || "");
        setShowSuggestions(true);
      } else if (typeof result === "string") {
        setSuggestions([result]);
        setSubGroupNameInHindi(result);
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error("Transliteration error:", err);
    }
  };

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    if (!subGroupName.trim()) { setSubGroupValidation(true); return false; }
    if (!groupId) { setSelectGroupValidation(true); setSubGroupValidation(false); return false; }
    return true;
  };

  // ─── Reset current form fields only ──────────────────────────────────────
  const resetFields = () => {
    setSubGroupName("");
    setSubGroupNameInHindi("");
    setHindiSuggestion("");
    setGroupName("");
    setGroupId("");
    setGroupType("");
    setShowField(false);
    setSubGroupValidation(false);
    setSelectGroupValidation(false);
    setEditingBatchIndex(-1);
  };

  // ─── Add More (batch) ─────────────────────────────────────────────────────
  const handleAddMore = () => {
    if (!validate()) return;

    const newSubGroup = {
      subGroupName,
      subGroupNameInHindi,
      groupId,
      groupName,
      groupType,
    };

    if (editingBatchIndex >= 0) {
      setSubGroupBatch((prev) => {
        const updated = [...prev];
        updated[editingBatchIndex] = newSubGroup;
        return updated;
      });
    } else {
      setSubGroupBatch((prev) => [...prev, newSubGroup]);
    }

    resetFields();
  };

  // ─── Edit a batch item ────────────────────────────────────────────────────
  const handleEditBatchItem = (index) => {
    const item = subGroupBatch[index];
    setEditingBatchIndex(index);
    setSubGroupName(item.subGroupName);
    setSubGroupNameInHindi(item.subGroupNameInHindi || "");
    setHindiSuggestion(item.subGroupName);
    setGroupName(item.groupName);
    setGroupId(item.groupId);
    setGroupType(item.groupType);
    setSubGroupValidation(false);
    setSelectGroupValidation(false);
  };

  // ─── Remove a batch item ──────────────────────────────────────────────────
  const handleRemoveBatchItem = (index) => {
    setSubGroupBatch((prev) => prev.filter((_, i) => i !== index));
    if (editingBatchIndex === index) {
      resetFields();
    } else if (editingBatchIndex > index) {
      setEditingBatchIndex((prev) => prev - 1);
    }
  };

  // ─── Save All ─────────────────────────────────────────────────────────────
  const handleSaveAll = () => {
    if (isEditMode) {
      // Edit mode: just validate and call parent save
      if (!validate()) return;
      handleSave();
      return;
    }

    if (subGroupBatch && subGroupBatch.length > 0) {
      if (subGroupName.trim()) {
        if (!validate()) return;
        const current = { subGroupName, subGroupNameInHindi, groupId, groupName, groupType };
        if (editingBatchIndex >= 0) {
          const updated = [...subGroupBatch];
          updated[editingBatchIndex] = current;
          setSubGroupBatch(updated);
        } else {
          setSubGroupBatch((prev) => [...prev, current]);
        }
      }
      handleSave();
    } else {
      if (!validate()) return;
      handleSave();
    }
  };

  const batchLength = subGroupBatch?.length ?? 0;

  return (
    <div className="bg-white p-2 rounded shadow-lg w-96">
      {/* Header */}
      <div className="bg-theme-headerBg text-theme-color text-center rounded-1 py-1 mb-2 flex justify-between items-center px-2">
        <div className="font-medium py-[2px]">
          {isEditMode ? t("editSubGroup") : t("addNewSubGroup")}
        </div>
        {/* {batchLength > 0 && (
          <span className="text-xs bg-white text-theme-headerBg px-2 py-[2px] rounded font-medium">
            {batchLength} added
          </span>
        )} */}
      </div>

      {/*  Batch List */}
      {batchLength > 0 && (
        <div className="mb-3">
          <div className="text-xs font-medium text-theme-headerBg mb-1 ">
            {t("listOfAddedSubGroups") || "Added Sub-Groups"} ({batchLength})
          </div>
          <div className="flex flex-col gap-1 max-h-32 overflow-y-auto pr-1">
            {subGroupBatch.map((item, index) => (
              <div
                key={index}
                className={`flex items-center justify-between rounded border px-2 py-[4px] text-xs transition-all
                  ${editingBatchIndex === index
                    ? "border-theme-headerBg bg-theme-headerBg/10 font-medium"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                  }`}
              >
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <span className="text-gray-400 w-4 shrink-0">{index + 1}.</span>
                  <span className="truncate font-medium text-gray-800 capitalize">
                    {item.subGroupName}
                    {item.subGroupNameInHindi && (
                      <span className="text-gray-400 font-normal ml-1">({item.subGroupNameInHindi})</span>
                    )}
                  </span>
                  <span className="text-gray-400 shrink-0 hidden sm:inline">
                    {item.groupName && `• ${item.groupName}`}
                  </span>
                </div>
                <div className="flex items-center gap-1 ml-1 shrink-0">
                  {editingBatchIndex === index ? (
                    <span className="text-theme-headerBg italic">{t("editing") || "editing..."}</span>
                  ) : (
                    <button type="button" onClick={() => handleEditBatchItem(index)}
                      className="text-gray-500 hover:text-theme-headerBg p-1 rounded transition-colors">
                      <FaEdit className="text-xs" />
                    </button>
                  )}
                  <button type="button" onClick={() => handleRemoveBatchItem(index)}
                    className="text-gray-400 hover:text-red-500 p-1 rounded transition-colors">
                    <ImCross className="text-[10px]" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editing banner */}
      {/* {editingBatchIndex >= 0 && (
        <div className="mb-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex justify-between items-center">
          <span>✏️ Editing sub-group #{editingBatchIndex + 1}</span>
          <button type="button" onClick={resetFields} className="underline hover:text-blue-900">
            Cancel Edit
          </button>
        </div>
      )} */}

      {/* SubGroup Name */}
      <label className="text-sm font-medium text-black">{t("subGroupName")}</label>
      <input
        type="text"
        value={subGroupName ?? ""}
        onChange={handleSubGroupNameChange}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder="Enter sub-group name"
        className={`border ${subGroupValidation ? "border-danger" : "border-gray-300"} 
                   rounded w-full p-1 px-2 capitalize`}
      />
      {subGroupValidation && (
        <div className="text-red-600 text-sm px-2 my-1">{t("pleaseSelectSubGroup")}</div>
      )}

      {/* Hindi edit field */}
      {showField && (
        <>
          <label className="font-medium mt-2 text-sm">{t("subGroupName")}</label>
          <div className="flex">
            <input type="text" value={hindiSuggestion ?? ""} onChange={handleConvertNameInHindi} nBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Enter name"
              className="border outline-none rounded-s w-full p-1 px-2 capitalize" />
            <div className="border px-2 ms-auto w-8 cursor-pointer rounded-e bg-white">
              <ImCross className="mt-2 text-theme-createLedgerColor" onClick={() => setShowField(false)} />
            </div>
          </div>
        </>
      )}

      {/* Translated value */}
      {language !== "en" && (
        <div className="relative">
          <label className="block text mt-2 font-medium text-sm">
            {language === "hi" ? t("translateinHindi") : t("translateinPunjabi")}
          </label>
          <div className="flex">
            <input type="text" value={subGroupNameInHindi ?? ""} disabled placeholder="Translate Value"
              className="border px-2 py-1 rounded-s w-full cursor-not-allowed bg-gray-100 capitalize" />
            <div className="border px-2 ms-auto w-8 cursor-pointer rounded-e bg-white">
              <FaEdit className="mt-2" onClick={() => setShowField(true)} />
            </div>
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute top-full left-0 w-full bg-white border rounded shadow z-50 text-sm max-h-32 overflow-y-auto p-0">
              {suggestions.map((s, i) => (
                <li
                  key={i}
                  className="px-2 py-1 cursor-pointer border-dashed border-b hover:bg-theme-headerBg hover:text-theme-color"
                  onClick={() => {
                    setSubGroupNameInHindi(s);
                    setShowSuggestions(false);
                  }}
                >
                  {s}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Group Dropdown */}
      <label className="font-medium text-black mt-2 text-sm">{t("groupName")}</label>
      <Dropdown
        value={
          groupName
            ? `${groupName}${language !== "en" && options.find((opt) => opt.label === groupName)?.labelInHindi
              ? ` (${options.find((opt) => opt.label === groupName)?.labelInHindi})`
              : ""
            }`
            : ""
        }
        placeholder={t("selectGroup")}
        hasError={selectGroupValidation}
        options={options}
        onSelect={handleSelectGroup}
        extraOption={{ label: t("addNewGroup"), onClick: handleAddNewGroup }}
      />
      {selectGroupValidation && (
        <div className="text-red-600 text-sm px-2 mb-1">{t("pleaseSelectGroup")}</div>
      )}

      {/* Type (readonly) */}
      <label className="text-sm font-medium text-black">{t("selectType")}</label>
      <input
        type="text"
        value={groupType ?? ""}
        readOnly
        placeholder="Group type will appear here"
        className="border border-gray-300 rounded w-full p-2 py-1 mb-3 bg-gray-100 capitalize"
      />

      {/* Action Buttons */}
      <div className="flex justify-end items-center">
        {/* <div>
          {batchLength > 0 && (
            <span className="text-xs text-gray-500">
              Total: {batchLength} sub-group{batchLength > 1 ? "s" : ""}
            </span>
          )}
        </div> */}
        <div className="flex gap-2">
          <CancelButton onClick={handleSubGroupCancel} text={t("cancel")} />
          {/* Add More: only in create mode and addMoreButton=true */}
          {addMoreButton && !isEditMode && (
            <SaveButton
              onClick={handleAddMore}
              text={editingBatchIndex >= 0 ? (t("edit") || "Update") : (t("addMore") || "Add More")}
            />
          )}
          <SaveButton
            onClick={handleSaveAll}
            text={
              batchLength > 0
                ? t("saveAll")
                : loading
                  ? t("saving") || "Saving..."
                  : t("save")
            }
          />
        </div>
      </div>
    </div>
  );
}